'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale, useTranslations } from 'next-intl';
import type * as React from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { AnimatedProgressCircle } from '@/components/ui/animated-progress-circle';
import { Progress } from '@/components/ui/app-progress';
import type { IconSvgElement } from '@/components/ui/icon';
import { Icon } from '@/components/ui/icon';
import { Camera01Icon, CheckmarkCircle02Icon, CheckmarkCircle02IconSolid, Edit03IconSolid, FireIconSolid, UserMultiple02IconSolid } from '@/components/ui/icon/glyphs';
import { Panel, PanelContent, PanelHeader } from '@/components/ui/panel';
import { StatusPill } from '@/components/ui/status-pill';
import { confettiOriginFromElement } from '@/lib/ui/confetti';
import { cn } from '@/lib/ui/utils';
import { PointsChip } from './points-chip';
interface Milestone {
  id: string;
  title: string;
  description: string;
  /** 0 streak · 1 quests · 2 referrals · 3 posts · 4 clips */
  track: number;
  targetCount: string;
  rewardPoints: string;
  progress: string;
  reached: boolean;
  claimed: boolean;
  claimedAt: string | null;
}
interface MilestonesPanelProps {
  milestones: Milestone[];
  /** `origin` is the clicked button's viewport position, so the success
   *  confetti bursts from the button (see the trade card). */
  onClaim: (id: string, origin?: {
    x: number;
    y: number;
  }) => void;
  claimingId?: string | null;
  /** Rendered above the progress summary — hosts the Leaderboard/Milestones
   *  segmented switch + info icon when the dashboard owns the panel header. */
  headerSlot?: React.ReactNode;
  /** Rendered under the progress summary — hosts the Claim all action. */
  claimAllSlot?: React.ReactNode;
  className?: string;
}

// track → committed glyph (one per achievement track).
function trackIcon(track: number): IconSvgElement {
  switch (track) {
    case 1:
      return CheckmarkCircle02IconSolid;
    case 2:
      return UserMultiple02IconSolid;
    case 3:
      return Edit03IconSolid;
    case 4:
      return Camera01Icon;
    default:
      return FireIconSolid;
  }
}
export function MilestonesPanel(t0) {
  const $ = _c(84);
  const {
    milestones,
    onClaim,
    claimingId,
    headerSlot,
    claimAllSlot,
    className
  } = t0;
  const t = useTranslations("rewards");
  const locale = useLocale();
  let t1;
  if ($[0] !== locale) {
    t1 = n => n.toLocaleString(locale);
    $[0] = locale;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const fmt = t1;
  const total = milestones.length;
  let T0;
  let T1;
  let t10;
  let t11;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[2] !== className || $[3] !== fmt || $[4] !== headerSlot || $[5] !== milestones || $[6] !== t || $[7] !== total) {
    const completed = milestones.filter(_temp).length;
    let t12;
    let t13;
    if ($[20] !== className || $[21] !== fmt || $[22] !== headerSlot || $[23] !== milestones || $[24] !== t) {
      const totalPts = milestones.reduce(_temp2, 0);
      const claimedPts = milestones.filter(_temp3).reduce(_temp4, 0);
      const pct = totalPts > 0 ? Math.round(claimedPts / totalPts * 100) : 0;
      T1 = Panel;
      t10 = "milestones-panel";
      if ($[36] !== className) {
        t11 = cn("lg:overflow-hidden", className);
        $[36] = className;
        $[37] = t11;
      } else {
        t11 = $[37];
      }
      T0 = PanelHeader;
      t8 = "flex flex-col gap-3";
      t9 = headerSlot;
      t6 = "flex flex-col gap-2";
      const t14 = `${String(pct)}%`;
      let t15;
      if ($[38] !== t14) {
        t15 = {
          transform: "none",
          width: t14
        };
        $[38] = t14;
        $[39] = t15;
      } else {
        t15 = $[39];
      }
      if ($[40] !== pct || $[41] !== t15) {
        t7 = <Progress value={pct} className="h-1.5 bg-background-tertiary" indicatorClassName="rounded-full bg-[image:linear-gradient(90deg,var(--brand-400),var(--brand-600))]" indicatorStyle={t15} />;
        $[40] = pct;
        $[41] = t15;
        $[42] = t7;
      } else {
        t7 = $[42];
      }
      t4 = "flex items-center justify-between gap-2 text-caption";
      t12 = "text-foreground-secondary";
      t13 = t("milestones.ptsOf", {
        claimed: fmt(claimedPts),
        total: fmt(totalPts)
      });
      $[20] = className;
      $[21] = fmt;
      $[22] = headerSlot;
      $[23] = milestones;
      $[24] = t;
      $[25] = T0;
      $[26] = T1;
      $[27] = t10;
      $[28] = t11;
      $[29] = t12;
      $[30] = t13;
      $[31] = t4;
      $[32] = t6;
      $[33] = t7;
      $[34] = t8;
      $[35] = t9;
    } else {
      T0 = $[25];
      T1 = $[26];
      t10 = $[27];
      t11 = $[28];
      t12 = $[29];
      t13 = $[30];
      t4 = $[31];
      t6 = $[32];
      t7 = $[33];
      t8 = $[34];
      t9 = $[35];
    }
    if ($[43] !== t12 || $[44] !== t13) {
      t5 = <span className={t12}>{t13}</span>;
      $[43] = t12;
      $[44] = t13;
      $[45] = t5;
    } else {
      t5 = $[45];
    }
    t2 = "text-foreground-secondary";
    t3 = t("milestones.completedCount", {
      completed,
      total
    });
    $[2] = className;
    $[3] = fmt;
    $[4] = headerSlot;
    $[5] = milestones;
    $[6] = t;
    $[7] = total;
    $[8] = T0;
    $[9] = T1;
    $[10] = t10;
    $[11] = t11;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    T0 = $[8];
    T1 = $[9];
    t10 = $[10];
    t11 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
    t9 = $[19];
  }
  let t12;
  if ($[46] !== t2 || $[47] !== t3) {
    t12 = <span className={t2}>{t3}</span>;
    $[46] = t2;
    $[47] = t3;
    $[48] = t12;
  } else {
    t12 = $[48];
  }
  let t13;
  if ($[49] !== t12 || $[50] !== t4 || $[51] !== t5) {
    t13 = <div className={t4}>{t5}{t12}</div>;
    $[49] = t12;
    $[50] = t4;
    $[51] = t5;
    $[52] = t13;
  } else {
    t13 = $[52];
  }
  let t14;
  if ($[53] !== t13 || $[54] !== t6 || $[55] !== t7) {
    t14 = <div className={t6}>{t7}{t13}</div>;
    $[53] = t13;
    $[54] = t6;
    $[55] = t7;
    $[56] = t14;
  } else {
    t14 = $[56];
  }
  let t15;
  if ($[57] !== claimAllSlot || $[58] !== t14 || $[59] !== t8 || $[60] !== t9) {
    t15 = <div className={t8}>{t9}{t14}{claimAllSlot}</div>;
    $[57] = claimAllSlot;
    $[58] = t14;
    $[59] = t8;
    $[60] = t9;
    $[61] = t15;
  } else {
    t15 = $[61];
  }
  let t16;
  if ($[62] !== T0 || $[63] !== t15) {
    t16 = <T0>{t15}</T0>;
    $[62] = T0;
    $[63] = t15;
    $[64] = t16;
  } else {
    t16 = $[64];
  }
  let t17;
  if ($[65] !== claimingId || $[66] !== fmt || $[67] !== milestones || $[68] !== onClaim || $[69] !== t) {
    let t18;
    if ($[71] !== claimingId || $[72] !== fmt || $[73] !== onClaim || $[74] !== t) {
      t18 = m_3 => {
        const target = Number(m_3.targetCount);
        const itemPct = target > 0 ? Math.min(100, Number(m_3.progress) / target * 100) : 0;
        return <div key={m_3.id} data-testid={`milestone-${m_3.id}`} className="flex flex-col gap-2.5 rounded-panel-row border px-3 py-2.5"><div className="flex items-center gap-2.5"><span className="flex size-7 shrink-0 items-center justify-center rounded-control bg-background-tertiary"><Icon icon={trackIcon(m_3.track)} className="size-3.5 text-foreground-secondary" /></span><span className="min-w-0 flex-1 truncate text-body text-foreground-primary">{m_3.title}</span><span className="flex shrink-0 items-center gap-1.5"><span className="text-caption font-medium tabular-nums text-foreground-secondary">{m_3.progress}/{m_3.targetCount}</span><AnimatedProgressCircle progress={itemPct} diameter={20} strokeWidth={4} className="shrink-0" /></span></div><div className="flex items-center justify-between gap-2"><p className="min-w-0 text-caption text-foreground-secondary">{m_3.description}</p><PointsChip>{t("milestones.pts", {
                pts: fmt(Number(m_3.rewardPoints))
              })}</PointsChip></div>{m_3.claimed ? <StatusPill tone="success" size="sm" className="w-full justify-center"><Icon icon={CheckmarkCircle02Icon} className="size-3.5" />{t("milestones.claimed")}</StatusPill> : m_3.reached ? <AnimatedButton data-testid={`claim-milestone-${m_3.id}`} className="w-full" onClick={e => onClaim(m_3.id, confettiOriginFromElement(e.currentTarget))} disabled={claimingId === m_3.id} loading={claimingId === m_3.id}>{claimingId === m_3.id ? t("milestones.claiming") : t("milestones.claimWithPts", {
              pts: fmt(Number(m_3.rewardPoints))
            })}</AnimatedButton> : <AnimatedButton variant="secondary" className="w-full" disabled={true}>{t("milestones.claim")}</AnimatedButton>}</div>;
      };
      $[71] = claimingId;
      $[72] = fmt;
      $[73] = onClaim;
      $[74] = t;
      $[75] = t18;
    } else {
      t18 = $[75];
    }
    t17 = milestones.map(t18);
    $[65] = claimingId;
    $[66] = fmt;
    $[67] = milestones;
    $[68] = onClaim;
    $[69] = t;
    $[70] = t17;
  } else {
    t17 = $[70];
  }
  let t18;
  if ($[76] !== t17) {
    t18 = <PanelContent className="flex flex-col gap-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">{t17}</PanelContent>;
    $[76] = t17;
    $[77] = t18;
  } else {
    t18 = $[77];
  }
  let t19;
  if ($[78] !== T1 || $[79] !== t10 || $[80] !== t11 || $[81] !== t16 || $[82] !== t18) {
    t19 = <T1 data-testid={t10} className={t11}>{t16}{t18}</T1>;
    $[78] = T1;
    $[79] = t10;
    $[80] = t11;
    $[81] = t16;
    $[82] = t18;
    $[83] = t19;
  } else {
    t19 = $[83];
  }
  return t19;
}
function _temp4(a_0, m_2) {
  return a_0 + Number(m_2.rewardPoints);
}
function _temp3(m_1) {
  return m_1.claimed;
}
function _temp2(a, m_0) {
  return a + Number(m_0.rewardPoints);
}
function _temp(m) {
  return m.claimed;
}